<script setup lang="ts">
import PartFooterNavigation from '@parts/part-footer-navigation.vue';
import PartTitleHead from '@parts/part-title-head.vue';
import PartOutisdeLinks, {OutsideLink} from '@parts/part-outisde-links.vue';
import SectionSettings from '@sections/section-settings.vue';
import ButtonPrimary from '@buttons/button-primary.vue';

import { useI18n } from 'vue-i18n';
import { useSettings } from '@composables/settings';
import {computed, onBeforeMount, ref} from 'vue';
import CoreLoader from "@core/core-loader.vue";
import {useSettingsApi} from "@store/api/settingsApi";
import {AppChoose, AppInfo} from "@store/api/interfaces";
import {useOS} from "@composables/os";
import router from "@/router";

const { t } = useI18n();
const {
	goToBack,
	currentGroup,
	checkCurrentPageAccess,
	selection,
} = useSettings();

const osGetter = useOS();
const settingsApi = useSettingsApi();
const isLoading = ref<boolean>(false);
const loaderText = ref<string>(t('common.loader.apps'));
const listApps = ref<AppInfo[]>([]);
const choosedApps = ref<AppChoose[]>([]);
const choosedApp = ref<AppInfo|null|undefined>(null);

const fields = computed(() => {
	return {
		title: t('settingsOS.title'),
    subtitle: t('settingsOS.subtitle'),
		list: listApps.value
			.map(app => app.instruction_link ? {
				url: app.instruction_link,
				title: app.platform_name,
				label: app.name
			} : null)
			.filter((item) => item !== null),
		buttonInstalled: {
			title: choosedApp ? t('common.buttons.already_installed') : t('common.buttons.installed'),
		},
    buttonHelp: {
      title: t('common.buttons.need_help'),
    },
		buttonBack: {
			title: t('common.buttons.back'),
		},
	}
});

const handleBeforeNavigate = async (item: OutsideLink): Promise<void> => {
	const app = listApps.value.find((app) => app.instruction_link === item.url && app.platform_name === item.title)
	if (app) {
    selection.app = app;
	}
};

onBeforeMount(async () => {
	loaderText.value = t('common.loader.apps');
	isLoading.value = true;
	listApps.value = await settingsApi.getApps();

	choosedApps.value = await settingsApi.getAppsChoose();
  const app = await getChoosedApp();
  if (app) {
    selection.app = app;
  }

	isLoading.value = false;
});

const getChoosedApp = async (): Promise<AppInfo|null> => {
  const os = await osGetter.get();
  let resChoosedApp = choosedApps.value.find(app => app.platform == os);
  if (resChoosedApp) {
    choosedApp.value = listApps.value.find((app) => app.platform_code === resChoosedApp?.platform && app.code === resChoosedApp?.app);

    if (choosedApp.value) {
      return choosedApp.value;
    }
  }

  return null;
}

const successHandler = async () => {
  if (selection.app) {
    isLoading.value = true;
    await settingsApi.saveAppChoose({app: selection.app.code, platform: selection.app.platform_code});
  }

  //region Запуск приложения
  //const osValue = await os.get();

  /*switch (osValue) {
    case 'ios':
    case 'macos_m':
      if (selection.app?.code === 'streisand') {
        runVpnApp.streisand.import(fields.value.key.value);
      }
      break;
    case 'macos_intel':
      if (selection.app?.code === 'v2box') {
        runVpnApp.v2box.open();
      }
      if (selection.app?.code === 'hiddify') {
        runVpnApp.hiddify.open();
      }
      break;

    case 'android':
      if (selection.app?.code === 'v2ray_ng') {
        runVpnApp.v2rayNg.open();
      }
      break
  }*/
  //endregion

  void router.replace("/");
}

checkCurrentPageAccess();
</script>

<template>
	<main class="page page-os">
		<section-settings
			v-if="currentGroup"
		>
			<transition
				appear
				name="fade"
				style="--animation-delay: 0.1s"
			>
				<part-title-head
					v-if="fields.title"
					:title="fields.title"
					:subtitle="fields.subtitle"
					title-font="font-18-pro-medium"
					sub-title-font="font-regular"
					class="page-os__content"
				></part-title-head>
			</transition>

			<!--
				Для <transition> нужно задать продолжительность анимации: animation-delay.
				Через "--animation-count" передаем количество элементов в списке, для построения продолжительсности.
			-->
			<transition
				v-if="!isLoading"
				appear
				name="show-up-container-items"
				:style="`--animation-item-delay: 0.2s;
				--animation-duration: 0.6s;
				--animation-count: ${listApps.length || 0}`"
			>
				<part-outisde-links
					:list="fields.list"
					:icon="{name: 'align-bottom', viewBox: '0 0 16 16'}"
					:beforeNavigate="handleBeforeNavigate"
					class="page-os__list"
				>
				</part-outisde-links>
			</transition>
			<transition
				v-else
				appear
				name="show-up-container-items"
				:style="`--animation-delay: 0.3s;
				--animation-item-delay: 0.2s;
				--animation-duration: 0.6s;`"
			>
				<core-loader
					:title="loaderText"
				/>
			</transition>

			<template v-if="!isLoading">
				<part-footer-navigation
					v-if="currentGroup?.list?.length > 1"
					show-back-button
					class="page-os__footer"
				>
					<button-primary
						@click.native="successHandler"
						:icon="{name: 'arrowRight', viewBox: '0 0 40 40'}"
						class="page-os__finish"
					>
						{{ fields?.buttonInstalled?.title }}
					</button-primary>
				</part-footer-navigation>
				<button-primary
					v-else
					pink
					@click.native="goToBack"
					class="page-os__finish"
				>
					{{ fields?.buttonBack?.title }}
				</button-primary>
			</template>
		</section-settings>
	</main>
</template>

<style lang="scss">
.page-os {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;

	&__content {
		--title-gap: 1.6rem;
		margin-bottom: 2.4rem;
	}

	&__list {
		margin-bottom: 2rem;
		@include min-desktop {
			margin-bottom: 6.4rem;
		}
	}

	&__finish {
		width: 100%;
		margin-top: auto;
	}
}
</style>
